import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../store/useAuthStore'
import { supabase } from '../lib/supabaseClient'
import Header from '../components/Header'

const EmployeeDashboard = () => {
  const { profile } = useAuthStore()
  const [activeTab, setActiveTab] = useState('overview')
  const [projects, setProjects] = useState([])
  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(true)

  // Fetch employee data
  useEffect(() => {
    fetchEmployeeData()
  }, [])

  const fetchEmployeeData = async () => {
    try {
      setLoading(true)
      
      // Fetch projects where employee is assigned - Fixed method
      console.log('🔍 Fetching projects for employee:', profile?.id)
      
      // First get project IDs from project_members
      const { data: membersData, error: membersError } = await supabase
        .from('project_members')
        .select('project_id')
        .eq('user_id', profile?.id)

      console.log('📊 Project members data:', membersData)
      console.log('❌ Project members error:', membersError)

      if (!membersError && membersData && membersData.length > 0) {
        // Extract project IDs
        const projectIds = membersData.map(member => member.project_id)
        console.log('📋 Project IDs:', projectIds)

        // Fetch projects using the IDs
        const { data: projectsData, error: projectsError } = await supabase
          .from('projects')
          .select('*')
          .in('id', projectIds)

        console.log('📊 Projects data:', projectsData)
        console.log('❌ Projects error:', projectsError)

        if (!projectsError && projectsData) {
          console.log('✅ Projects found:', projectsData.length)
          setProjects(projectsData)
        } else {
          console.log('⚠️ No projects found')
          setProjects([])
        }
      } else {
        console.log('⚠️ No project members found')
        setProjects([])
      }

      // Fetch tasks assigned to employee
      console.log('🔍 Fetching tasks for employee:', profile?.id)
      const { data: tasksData, error: tasksError } = await supabase
        .from('tasks')
        .select('*')
        .eq('assignee_id', profile?.id)
        .order('created_at', { ascending: false })

      console.log('📋 Tasks data:', tasksData)
      console.log('❌ Tasks error:', tasksError)

      if (!tasksError && tasksData) {
        console.log('✅ Tasks found:', tasksData.length)
        setTasks(tasksData)
      } else {
        console.log('⚠️ No tasks found or error occurred')
        setTasks([])
      }

    } catch (error) {
      console.error('Error fetching employee data:', error)
    } finally {
      setLoading(false)
    }
  }

  // Update task status
  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      console.log('📝 Updating task status:', taskId, newStatus)
      
      const { data, error } = await supabase
        .from('tasks')
        .update({ status: newStatus })
        .eq('id', taskId)
        .select()

      if (error) {
        console.error('❌ Error updating task:', error)
        alert('Failed to update task: ' + error.message)
        return
      }

      console.log('✅ Task updated successfully:', data)

      // Update local state
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === taskId 
            ? { ...task, status: newStatus }
            : task
        )
      )

      alert('Task status updated successfully!')

    } catch (error) {
      console.error('❌ Error updating task:', error)
      alert('Failed to update task: ' + error.message)
    }
  }

  // Debug function to check project assignments
  const debugProjectAssignments = async () => {
    try {
      console.log('🔍 DEBUG: Checking project assignments for employee:', profile?.id)
      
      // Check project_members table
      const { data: membersData, error: membersError } = await supabase
        .from('project_members')
        .select('*')
        .eq('user_id', profile?.id)

      console.log('📊 Project members data:', membersData)
      console.log('❌ Project members error:', membersError)

      if (!membersError && membersData && membersData.length > 0) {
        // Extract project IDs
        const projectIds = membersData.map(member => member.project_id)
        console.log('📋 Project IDs to check:', projectIds)

        // Check each project individually
        for (const projectId of projectIds) {
          const { data: projectData, error: projectError } = await supabase
            .from('projects')
            .select('*')
            .eq('id', projectId)
            .single()

          console.log(`📊 Project ${projectId}:`, projectData)
          console.log(`❌ Project ${projectId} error:`, projectError)
        }
      }

      // Check all projects
      const { data: allProjects, error: allProjectsError } = await supabase
        .from('projects')
        .select('*')

      console.log('📊 All projects:', allProjects)
      console.log('❌ All projects error:', allProjectsError)

      // Check if employee exists in profiles
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', profile?.id)

      console.log('👤 Employee profile:', profileData)
      console.log('❌ Profile error:', profileError)

    } catch (error) {
      console.error('❌ Debug error:', error)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-gradient-to-br from-blue-800 to-blue-900 text-white shadow-lg p-6 flex flex-col">
          <div className="mb-8 text-center">
            <h2 className="text-2xl font-extrabold text-white">Employee Panel</h2>
            <p className="text-sm text-blue-200 mt-1">Welcome, {profile?.name}</p>
          </div>
          
          <nav className="flex-1 space-y-2">
            <button
              onClick={() => setActiveTab('overview')}
              className={`w-full flex items-center px-4 py-3 text-left rounded-lg transition-colors ${
                activeTab === 'overview' 
                  ? 'bg-blue-700 text-white' 
                  : 'text-blue-200 hover:bg-blue-700'
              }`}
            >
              <svg className="h-5 w-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v6H8V5z" />
              </svg>
              Overview
            </button>
            
            <button
              onClick={() => setActiveTab('projects')}
              className={`w-full flex items-center px-4 py-3 text-left rounded-lg transition-colors ${
                activeTab === 'projects' 
                  ? 'bg-blue-700 text-white' 
                  : 'text-blue-200 hover:bg-blue-700'
              }`}
            >
              <svg className="h-5 w-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              My Projects ({projects.length})
            </button>
            
            <button
              onClick={() => setActiveTab('tasks')}
              className={`w-full flex items-center px-4 py-3 text-left rounded-lg transition-colors ${
                activeTab === 'tasks' 
                  ? 'bg-blue-700 text-white' 
                  : 'text-blue-200 hover:bg-blue-700'
              }`}
            >
              <svg className="h-5 w-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              My Tasks ({tasks.length})
            </button>
            
            <button
              onClick={() => setActiveTab('time-tracking')}
              className={`w-full flex items-center px-4 py-3 text-left rounded-lg transition-colors ${
                activeTab === 'time-tracking' 
                  ? 'bg-blue-700 text-white' 
                  : 'text-blue-200 hover:bg-blue-700'
              }`}
            >
              <svg className="h-5 w-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Time Tracking
            </button>
            
            <button
              onClick={() => setActiveTab('files')}
              className={`w-full flex items-center px-4 py-3 text-left rounded-lg transition-colors ${
                activeTab === 'files' 
                  ? 'bg-blue-700 text-white' 
                  : 'text-blue-200 hover:bg-blue-700'
              }`}
            >
              <svg className="h-5 w-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              Files
            </button>
          </nav>
          
          <div className="mt-auto pt-6 border-t border-blue-700">
            <button
              onClick={debugProjectAssignments}
              className="w-full flex items-center px-4 py-3 text-left rounded-lg text-blue-200 hover:bg-blue-700 transition-colors mb-2"
            >
              <svg className="h-5 w-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Debug Projects
            </button>
            <Link 
              to="/" 
              className="w-full flex items-center px-4 py-3 text-left rounded-lg text-blue-200 hover:bg-blue-700 transition-colors"
            >
              <svg className="h-5 w-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Site
            </Link>
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex-1 p-8">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <>
                  <h1 className="text-3xl font-bold text-gray-900 mb-8">Employee Dashboard</h1>
                  
                  {/* Stats Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <div className="card">
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                          </svg>
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-600">Assigned Projects</p>
                          <p className="text-2xl font-semibold text-gray-900">{projects.length}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="card">
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                          <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-600">Completed Tasks</p>
                          <p className="text-2xl font-semibold text-gray-900">{tasks.filter(task => task.status === 'completed').length}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="card">
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                          <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-600">In Progress Tasks</p>
                          <p className="text-2xl font-semibold text-gray-900">{tasks.filter(task => task.status === 'in_progress').length}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="card">
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                          <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                          </svg>
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-600">Pending Tasks</p>
                          <p className="text-2xl font-semibold text-gray-900">{tasks.filter(task => task.status === 'pending').length}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Recent Projects */}
                  <div className="card mb-8">
                    <h2 className="text-xl font-semibold mb-4">Recent Projects</h2>
                    <div className="space-y-3">
                      {projects.slice(0, 3).map(project => (
                        <div key={project.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-2">
                            <h3 className="font-medium">{project.name}</h3>
                            <span className={`text-sm px-2 py-1 rounded ${
                              project.status === 'active' ? 'text-green-600 bg-green-100' :
                              project.status === 'planning' ? 'text-blue-600 bg-blue-100' :
                              'text-gray-600 bg-gray-100'
                            }`}>
                              {project.status || 'Active'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{project.description}</p>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-500">
                              Priority: {project.priority || 'Medium'}
                            </span>
                            <Link to={`/project/${project.id}`} className="text-sm text-blue-600 hover:text-blue-700">
                              View Details →
                            </Link>
                          </div>
                        </div>
                      ))}
                      {projects.length === 0 && (
                        <p className="text-gray-500 text-center py-4">No projects assigned yet</p>
                      )}
                    </div>
                  </div>

                  {/* Recent Tasks */}
                  <div className="card">
                    <h2 className="text-xl font-semibold mb-4">Recent Tasks</h2>
                    <div className="space-y-3">
                      {tasks.slice(0, 5).map(task => (
                        <div key={task.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <h4 className="font-medium">{task.title}</h4>
                            <p className="text-sm text-gray-600">{task.description}</p>
                          </div>
                          <span className={`text-sm px-2 py-1 rounded ${
                            task.status === 'completed' ? 'text-green-600 bg-green-100' :
                            task.status === 'in_progress' ? 'text-yellow-600 bg-yellow-100' :
                            'text-red-600 bg-red-100'
                          }`}>
                            {task.status}
                          </span>
                        </div>
                      ))}
                      {tasks.length === 0 && (
                        <p className="text-gray-500 text-center py-4">No tasks assigned yet</p>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Projects Tab */}
              {activeTab === 'projects' && (
                <>
                  <h1 className="text-3xl font-bold text-gray-900 mb-8">My Projects</h1>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {projects.map(project => (
                      <div key={project.id} className="card">
                        <div className="flex justify-between items-start mb-4">
                          <h3 className="text-lg font-semibold">{project.name}</h3>
                          <span className={`text-sm px-2 py-1 rounded ${
                            project.status === 'active' ? 'text-green-600 bg-green-100' :
                            project.status === 'planning' ? 'text-blue-600 bg-blue-100' :
                            'text-gray-600 bg-gray-100'
                          }`}>
                            {project.status || 'Active'}
                          </span>
                        </div>
                        <p className="text-gray-600 mb-4">{project.description}</p>
                        <div className="flex justify-between items-center">
                          <div className="text-sm text-gray-500">
                            <p>Priority: {project.priority || 'Medium'}</p>
                            <p>Start: {project.start_date ? new Date(project.start_date).toLocaleDateString() : 'N/A'}</p>
                          </div>
                          <Link to={`/project/${project.id}`} className="btn-primary">
                            View Project
                          </Link>
                        </div>
                      </div>
                    ))}
                    {projects.length === 0 && (
                      <div className="col-span-2">
                        <div className="card text-center py-12">
                          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                          </svg>
                          <h3 className="text-lg font-medium text-gray-900 mb-2">No Projects Assigned</h3>
                          <p className="text-gray-500">You haven't been assigned to any projects yet.</p>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Tasks Tab */}
              {activeTab === 'tasks' && (
                <>
                  <h1 className="text-3xl font-bold text-gray-900 mb-8">My Tasks</h1>
                  <div className="space-y-4">
                    {tasks.map(task => (
                      <div key={task.id} className="card">
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="text-lg font-semibold">{task.title}</h3>
                          <span className={`text-sm px-2 py-1 rounded ${
                            task.status === 'completed' ? 'text-green-600 bg-green-100' :
                            task.status === 'in_progress' ? 'text-yellow-600 bg-yellow-100' :
                            'text-red-600 bg-red-100'
                          }`}>
                            {task.status}
                          </span>
                        </div>
                        <p className="text-gray-600 mb-3">{task.description}</p>
                        <div className="flex justify-between items-center text-sm text-gray-500 mb-3">
                          <span>Priority: {task.priority || 'Medium'}</span>
                          <span>Due: {task.due_date ? new Date(task.due_date).toLocaleDateString() : 'No due date'}</span>
                        </div>
                        
                        {/* Task Status Update Buttons */}
                        <div className="flex space-x-2">
                          {task.status === 'pending' && (
                            <button
                              onClick={() => updateTaskStatus(task.id, 'in_progress')}
                              className="px-3 py-1 bg-yellow-500 text-white text-sm rounded hover:bg-yellow-600 transition-colors"
                            >
                              Start Work
                            </button>
                          )}
                          {task.status === 'in_progress' && (
                            <>
                              <button
                                onClick={() => updateTaskStatus(task.id, 'completed')}
                                className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600 transition-colors"
                              >
                                Mark Complete
                              </button>
                              <button
                                onClick={() => updateTaskStatus(task.id, 'pending')}
                                className="px-3 py-1 bg-gray-500 text-white text-sm rounded hover:bg-gray-600 transition-colors"
                              >
                                Back to Pending
                              </button>
                            </>
                          )}
                          {task.status === 'completed' && (
                            <button
                              onClick={() => updateTaskStatus(task.id, 'in_progress')}
                              className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors"
                            >
                              Reopen Task
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                    {tasks.length === 0 && (
                      <div className="card text-center py-12">
                        <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Tasks Assigned</h3>
                        <p className="text-gray-500">You don't have any tasks assigned yet.</p>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Time Tracking Tab */}
              {activeTab === 'time-tracking' && (
                <>
                  <h1 className="text-3xl font-bold text-gray-900 mb-8">Time Tracking</h1>
                  <div className="card text-center py-12">
                    <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Time Tracking Coming Soon</h3>
                    <p className="text-gray-500">Time tracking functionality will be available soon.</p>
                  </div>
                </>
              )}

              {/* Files Tab */}
              {activeTab === 'files' && (
                <>
                  <h1 className="text-3xl font-bold text-gray-900 mb-8">Files</h1>
                  <div className="card text-center py-12">
                    <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">File Management Coming Soon</h3>
                    <p className="text-gray-500">File management functionality will be available soon.</p>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default EmployeeDashboard
